import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Layers, Truck, RefreshCcw, ChevronRight, AlertTriangle, ShieldAlert } from "lucide-react";
import ProjectSearchBar, { ProjectSearchEmpty, projectMatches } from "@/components/shared/ProjectSearchBar";
import PendingQueueBanner from "@/components/PendingQueueBanner";

// Map stage → accent + short helper text. Colours stay inside the black/gold
// palette so the design guide stays intact.
const STAGE_META = {
  received:         { color: "border-gray-500/30 text-gray-300",       accent: "gray"   },
  fcp_work:         { color: "border-sky-400/40 text-sky-300",          accent: "sky"    },
  carcass_waiting:  { color: "border-amber-400/40 text-amber-300",      accent: "amber"  },
  carcass_assembly: { color: "border-[#d4af37]/40 text-[#d4af37]",      accent: "gold"   },
  manual_work:      { color: "border-violet-400/40 text-violet-300",    accent: "violet" },
  handed_over:      { color: "border-emerald-400/40 text-emerald-300",  accent: "emerald"},
};

export default function MyProjectsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Persist filter across back-navigation
  const [filter, setFilter] = useState(() => sessionStorage.getItem("myProjects.filter") || "all");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/execution/my-projects");
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live counts refresh every 30s — Production/Procurement/Purchase updates
  // roll into the supervisor view without a page reload.
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  // Persist filter every time it changes
  useEffect(() => { sessionStorage.setItem("myProjects.filter", filter); }, [filter]);

  // Restore scroll position on mount if user just returned via Back
  useEffect(() => {
    const saved = sessionStorage.getItem("myProjects.scrollY");
    if (saved) {
      requestAnimationFrame(() => window.scrollTo(0, parseInt(saved, 10) || 0));
      sessionStorage.removeItem("myProjects.scrollY");
    }
  }, []);

  const openProject = (project) => {
    // Save current scroll so Back returns to the same spot
    sessionStorage.setItem("myProjects.scrollY", String(window.scrollY));
    navigate(`/my-projects/${project.id}`);
  };

  const stages = data?.stages || [];
  const counts = data?.counts_by_stage || {};
  const total = data?.total || 0;
  const [q, setQ] = useState("");
  const stageFiltered = useMemo(() => {
    const all = data?.projects || [];
    if (filter === "all") return all;
    return all.filter((p) => p.my_project_stage === filter);
  }, [data, filter]);
  const projects = useMemo(
    () => stageFiltered.filter((p) => projectMatches(p, q)),
    [stageFiltered, q]
  );

  return (
    <div className="p-4 sm:p-10 space-y-6" data-testid="my-projects-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Site Execution · Personal Board</p>
          <h1 className="font-display text-3xl text-white mt-1">My Projects</h1>
          <p className="text-xs text-gray-500 mt-1">
            Stages update automatically from Production, Procurement and Purchase actions —
            you don't need to move anything by hand.
          </p>
        </div>
        <button onClick={load} disabled={loading} className="iz-btn-secondary text-xs flex items-center gap-2"
                data-testid="my-projects-refresh">
          <RefreshCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}/>
          Refresh
        </button>
      </div>

      {/* Deep-linked Pending Actions (Feb 2026) */}
      <PendingQueueBanner />

      {/* Stage summary cards — clickable filter chips */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3" data-testid="my-projects-stage-cards">
        <StageCard
          label="All Projects"
          count={total}
          active={filter === "all"}
          onClick={() => setFilter("all")}
          testid="stage-card-all"
          isAll
        />
        {stages.map((s) => (
          <StageCard
            key={s.key}
            label={s.label}
            count={counts[s.key] || 0}
            active={filter === s.key}
            onClick={() => setFilter(s.key)}
            testid={`stage-card-${s.key}`}
            meta={STAGE_META[s.key]}
          />
        ))}
      </div>

      <BlockedDashboard onOpen={(pid) => openProject({ id: pid })} />

      {/* Project list */}
      <div className="iz-card p-5" data-testid="my-projects-list">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-[#d4af37]"/>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
              {filter === "all"
                ? `All projects · ${projects.length}`
                : `${stages.find((s) => s.key === filter)?.label || filter} · ${projects.length}`}
            </p>
          </div>
        </div>

        <ProjectSearchBar
          value={q}
          onChange={setQ}
          resultCount={projects.length}
          totalCount={stageFiltered.length}
          testid="my-projects-search"
        />

        {loading && !data && (
          <p className="text-center text-gray-500 py-8 italic text-xs">Loading…</p>
        )}
        {!loading && projects.length === 0 && q.trim() && (
          <ProjectSearchEmpty query={q} testid="my-projects-search-empty" />
        )}
        {!loading && projects.length === 0 && !q.trim() && (
          <p className="text-center text-gray-500 py-8 italic text-xs">
            No projects{filter !== "all" ? " in this stage" : " assigned yet"}.
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {projects.map((p) => (
            <ProjectRow key={p.id} project={p} onOpen={() => openProject(p)} />
          ))}
        </div>
      </div>
    </div>
  );
}

function StageCard({ label, count, active, onClick, testid, isAll = false, meta = null }) {
  const activeCls = active
    ? "border-[#d4af37] bg-[#d4af37]/10 ring-1 ring-[#d4af37]/40"
    : "border-[#262626] hover:border-[#d4af37]/40";
  return (
    <button onClick={onClick}
            className={`iz-card p-3 text-left transition-all ${activeCls}`}
            data-testid={testid}>
      <p className={`text-[10px] font-mono uppercase tracking-widest mb-1 ${
        active ? "text-[#d4af37]"
        : isAll ? "text-white"
        : meta?.color?.split(" ")[1] || "text-gray-400"
      }`}>
        {label}
      </p>
      <p className={`font-display text-2xl ${active ? "text-[#d4af37]" : "text-white"}`}>
        {count}
      </p>
    </button>
  );
}

function ProjectRow({ project, onOpen }) {
  const stageMeta = STAGE_META[project.my_project_stage] || {};
  const stageLabel = STAGE_LABEL[project.my_project_stage] || project.my_project_stage;
  const completed = project.stages_completed || 0;
  const total = project.stages_total || 8;
  const blocked = project.blocked_stages || [];
  return (
    <button onClick={onOpen}
            className="iz-card p-4 text-left hover:border-[#d4af37]/40 transition-colors flex items-center gap-3"
            data-testid={`my-projects-row-${project.id}`}>
      <ProgressRing completed={completed} total={total} />
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm truncate">{project.client_name}{project.city ? ` · ${project.city}` : ""}</p>
        <p className="text-[11px] text-gray-500 truncate">{project.project_name}</p>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${stageMeta.color || "border-gray-500/30 text-gray-300"}`}>
            {stageLabel}
          </span>
          {project.dispatch_count > 0 && (
            <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border border-[#d4af37]/40 bg-[#d4af37]/10 text-[#d4af37] flex items-center gap-1">
              <Truck className="w-2.5 h-2.5"/> {project.dispatch_count} dispatched
            </span>
          )}
          {blocked.length > 0 && (
            <span
              className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border border-red-500/50 bg-red-500/10 text-red-300 flex items-center gap-1"
              title={blocked.map((b) => `${b.label}${b.reason ? ` — ${b.reason}` : ""}`).join(" · ")}
              data-testid={`blocked-badge-${project.id}`}
            >
              <AlertTriangle className="w-2.5 h-2.5"/> {blocked.length} blocked
            </span>
          )}
        </div>
        {blocked.length > 0 && blocked[0].reason && (
          <p className="text-[10px] text-red-300/80 italic mt-1 line-clamp-1">
            {blocked[0].label}: {blocked[0].reason}
          </p>
        )}
      </div>
      <ChevronRight className="w-4 h-4 text-gray-500 shrink-0"/>
    </button>
  );
}

function ProgressRing({ completed, total }) {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const r = 18;                             // radius
  const c = 2 * Math.PI * r;                // circumference
  const dash = (pct / 100) * c;
  return (
    <div className="relative w-11 h-11 shrink-0" data-testid="stage-progress-ring">
      <svg className="w-11 h-11 -rotate-90" viewBox="0 0 44 44">
        <circle cx="22" cy="22" r={r} className="fill-none stroke-[#262626]" strokeWidth="3" />
        <circle cx="22" cy="22" r={r}
                className="fill-none stroke-[#d4af37] transition-all"
                strokeWidth="3" strokeLinecap="round"
                strokeDasharray={`${dash} ${c}`} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span className="text-[11px] font-display text-white">{completed}</span>
        <span className="text-[8px] font-mono text-gray-500">of {total}</span>
      </div>
    </div>
  );
}

const STAGE_LABEL = {
  received: "Received",
  fcp_work: "False Ceiling / Plumbing",
  carcass_waiting: "Carcass Delivery Waiting",
  carcass_assembly: "Carcass Assembly",
  manual_work: "Manual Work",
  handed_over: "Handed Over",
};

function BlockedDashboard({ onOpen }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try {
        const { data } = await api.get("/execution/blocked-dashboard");
        if (!cancel) setItems(data.items || []);
      } catch { /* silent — widget is nice-to-have */ }
      finally { if (!cancel) setLoading(false); }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { cancel = true; clearInterval(t); };
  }, []);

  if (loading) return null;
  if (items.length === 0) return null;

  const visible = expanded ? items : items.slice(0, 3);
  return (
    <div className="iz-card p-5 border-red-500/40" data-testid="blocked-dashboard">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-red-400"/>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-red-400">
            Blocked Stages · {items.length}
          </p>
        </div>
        {items.length > 3 && (
          <button onClick={() => setExpanded((v) => !v)}
                  className="iz-btn-secondary text-[10px]"
                  data-testid="blocked-dashboard-toggle">
            {expanded ? "Collapse" : `Show all ${items.length}`}
          </button>
        )}
      </div>
      <div className="space-y-2">
        {visible.map((b) => (
          <button
            key={`${b.execution_project_id}-${b.stage_key}`}
            onClick={() => onOpen(b.execution_project_id)}
            className="w-full text-left border border-[#262626] hover:border-red-400/50 bg-[#0a0a0a] rounded-md p-3 flex items-center gap-3 transition-colors"
            data-testid={`blocked-item-${b.execution_project_id}-${b.stage_key}`}
          >
            <AlertTriangle className="w-4 h-4 text-red-400 shrink-0"/>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm truncate">
                {b.client_name}{b.city ? ` · ${b.city}` : ""}
                <span className="text-gray-500 font-mono text-[11px] ml-2">{b.project_name}</span>
              </p>
              <p className="text-[11px] text-gray-400 truncate">
                <span className="text-red-300 font-mono uppercase tracking-widest text-[10px] mr-2">{b.stage_label}</span>
                {b.reason || <span className="italic text-gray-600">no reason</span>}
              </p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Age</p>
              <p className={`font-display text-sm ${b.age_days >= 7 ? "text-red-400" : b.age_days >= 2 ? "text-amber-400" : "text-gray-300"}`}>
                {b.age_days != null ? `${b.age_days}d` : "—"}
              </p>
              {b.director_escalated && (
                <p className="text-[9px] font-mono uppercase text-red-400">escalated</p>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
